import React from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Tooltip as InfoTooltip } from '@/components/Tooltip';
import { cn } from '@/utils/classname';

import type {
  Proof,
  ProofBanner,
  ProofCodeBlock,
  ProofCodeSnippet,
  ProofElement,
  ProofGroup,
  ProofHeading,
  ProofHttpExchange,
  ProofImage,
  ProofKeyValue,
  ProofKeyValueRow,
  ProofList,
  ProofMetric,
  ProofParagraph,
  ProofReference,
  ProofSection,
  ProofSeverity,
  ProofTable,
  ProofTimeline,
} from './types';

// Severity → token class for borders, backgrounds, and text tints.
// Mirrors the legacy POE.tsx severity color choices for visual consistency.
const SEVERITY_BORDER: Record<ProofSeverity, string> = {
  critical: 'border-chariot-stroke-critical',
  high: 'border-chariot-stroke-high',
  medium: 'border-chariot-stroke-medium',
  low: 'border-chariot-stroke-low',
  info: 'border-chariot-stroke-info',
  success: 'border-chariot-stroke-info',
};

const SEVERITY_BG: Record<ProofSeverity, string> = {
  critical: 'bg-chariot-status-critical/10',
  high: 'bg-chariot-status-high/10',
  medium: 'bg-chariot-status-medium/10',
  low: 'bg-chariot-status-low/10',
  info: 'bg-chariot-status-info/10',
  success: 'bg-chariot-status-info/10',
};

const SEVERITY_TEXT: Record<ProofSeverity, string> = {
  critical: 'text-chariot-stroke-critical',
  high: 'text-chariot-text-orange',
  medium: 'text-chariot-text-yellow',
  low: 'text-chariot-text-secondary',
  info: 'text-chariot-stroke-info',
  success: 'text-chariot-stroke-info',
};

interface ProofRendererProps {
  proof: Proof;
  className?: string;
  // bare = suppress section chrome (border, header strip). Use when an
  // outer container (e.g. POE.tsx's sub-tab card) already provides the
  // card frame. The default `false` is appropriate for standalone
  // mounts (tests, embedded previews).
  bare?: boolean;
}

// ProofRenderer dispatches a v1 structured proof into per-element components.
// Visual conventions follow the legacy POE.tsx renderers so structured and
// legacy proof bodies look the same in the drawer.
export function ProofRenderer({ proof, className, bare }: ProofRendererProps) {
  // Defensive `?? []` here and at every other iteration point: Go marshals
  // nil slices as `null`, and older POE bodies still in S3 (pre-#5694) ship
  // sections with `"elements": null`. Iterating null with `for...of` or
  // `.map` throws "TypeError: a is not iterable" and the React Router error
  // boundary blows away the whole drawer. See proof_builder.go normalizeElements.
  const sections = proof.sections ?? [];
  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {sections.map((section, i) => (
        <SectionView key={i} section={section} bare={bare ?? false} />
      ))}
    </div>
  );
}

// SectionView either:
//   • renders only the elements when `bare` (POE.tsx provides card chrome)
//   • or renders a self-contained card with header strip otherwise
function SectionView({
  section,
  bare,
}: {
  section: ProofSection;
  bare: boolean;
}) {
  const elements = section.elements ?? [];
  if (bare || !section.title) {
    return <ElementList elements={elements} />;
  }
  return (
    <section className="border-chariot-stroke bg-chariot-background-secondary overflow-hidden rounded-lg border shadow-sm">
      <div className="border-chariot-stroke border-b px-4 py-2">
        <h3 className="text-chariot-text-secondary text-xs font-medium tracking-wider uppercase">
          {section.title}
        </h3>
      </div>
      <div className="px-4 py-3">
        <ElementList elements={elements} />
      </div>
    </section>
  );
}

// ElementList groups elements by Heading element. Each Heading introduces
// a sub-card containing the elements that follow (until the next Heading).
// Elements before the first Heading render bare. This lets sections with
// multiple sub-areas render as a stack of visually-separated sub-cards
// without needing to fan them out into separate top-level Sections.
function ElementList({ elements }: { elements: ProofElement[] }) {
  type Group = { heading?: ProofHeading; elements: ProofElement[] };
  const groups: Group[] = [{ elements: [] }];
  for (const el of elements ?? []) {
    if (el.type === 'heading' && el.heading) {
      groups.push({ heading: el.heading, elements: [] });
    } else {
      groups[groups.length - 1].elements.push(el);
    }
  }

  return (
    <div className="space-y-3">
      {groups.map((g, i) => {
        if (g.elements.length === 0 && !g.heading) return null;
        if (g.heading) {
          return (
            <SubCardView key={i} title={g.heading.text} elements={g.elements} />
          );
        }
        return <BareElementGroup key={i} elements={g.elements} />;
      })}
    </div>
  );
}

// SubCardView wraps a heading-led group in a bordered card with a header
// strip — visually distinct from the surrounding section card.
function SubCardView({
  title,
  elements,
}: {
  title: string;
  elements: ProofElement[];
}) {
  return (
    <div className="border-chariot-stroke bg-chariot-background-secondary overflow-hidden rounded-md border">
      <div className="border-chariot-stroke border-b px-3 py-1.5">
        <h4 className="text-chariot-text-secondary text-xs font-medium tracking-wider uppercase">
          {title}
        </h4>
      </div>
      <div className="px-3 py-2">
        <BareElementGroup elements={elements} />
      </div>
    </div>
  );
}

// BareElementGroup renders elements with metric coalescing, no card chrome.
function BareElementGroup({ elements }: { elements: ProofElement[] }) {
  const out: React.ReactNode[] = [];
  let metricBuf: ProofMetric[] = [];
  const flushMetrics = () => {
    if (metricBuf.length === 0) return;
    out.push(
      <MetricGrid key={`m-${out.length}`} metrics={metricBuf.slice()} />
    );
    metricBuf = [];
  };

  (elements ?? []).forEach((el, idx) => {
    if (el.type === 'metric' && el.metric) {
      metricBuf.push(el.metric);
      return;
    }
    flushMetrics();
    out.push(<ElementView key={idx} element={el} />);
  });
  flushMetrics();

  return <div className="space-y-3">{out}</div>;
}

function ElementView({ element }: { element: ProofElement }) {
  switch (element.type) {
    case 'heading':
      return element.heading ? <HeadingView heading={element.heading} /> : null;
    case 'paragraph':
      return element.paragraph ? (
        <ParagraphView paragraph={element.paragraph} />
      ) : null;
    case 'group':
      return element.group ? <GroupView group={element.group} /> : null;
    case 'key_value':
      return element.key_value ? <KeyValueView kv={element.key_value} /> : null;
    case 'table':
      return element.table ? <TableView table={element.table} /> : null;
    case 'list':
      return element.list ? <ListView list={element.list} /> : null;
    case 'code_block':
      return element.code_block ? (
        <CodeBlockView code={element.code_block} />
      ) : null;
    case 'code_snippet':
      return element.code_snippet ? (
        <CodeSnippetView snippet={element.code_snippet} />
      ) : null;
    case 'http_exchange':
      return element.http_exchange ? (
        <HttpExchangeView exchange={element.http_exchange} />
      ) : null;
    case 'banner':
      return element.banner ? <BannerView banner={element.banner} /> : null;
    case 'metric':
      return element.metric ? <MetricGrid metrics={[element.metric]} /> : null;
    case 'reference':
      return element.reference ? (
        <ReferenceView reference={element.reference} />
      ) : null;
    case 'image':
      return element.image ? <ImageView image={element.image} /> : null;
    case 'timeline':
      return element.timeline ? (
        <TimelineView timeline={element.timeline} />
      ) : null;
    default:
      return null;
  }
}

// HeadingView matches the legacy sub-section heading style:
// `text-chariot-text-primary pb-1 font-medium` for clear-but-quiet titles.
function HeadingView({ heading }: { heading: ProofHeading }) {
  const level = Math.min(Math.max(heading.level || 2, 1), 3);
  const Tag = level === 1 ? 'h4' : level === 2 ? 'h5' : 'h6';
  return (
    <Tag className="text-chariot-text-primary pb-1 text-sm font-medium">
      {heading.text}
    </Tag>
  );
}

function ParagraphView({ paragraph }: { paragraph: ProofParagraph }) {
  if (paragraph.markdown) {
    return (
      <div className="text-chariot-text-primary text-sm">
        <MarkdownPreview source={paragraph.text} />
      </div>
    );
  }
  return (
    <p className="text-chariot-text-primary text-sm whitespace-pre-wrap">
      {paragraph.text}
    </p>
  );
}

function GroupView({ group }: { group: ProofGroup }) {
  return (
    <div className="border-chariot-stroke overflow-hidden rounded-lg border">
      {group.title ? (
        <div className="border-chariot-stroke bg-chariot-background-primary border-b px-4 py-3">
          <div className="text-chariot-text-primary text-sm font-medium">
            {group.title}
          </div>
        </div>
      ) : null}
      <div className="space-y-3 px-4 py-3">
        <ElementList elements={group.elements ?? []} />
      </div>
    </div>
  );
}

// KeyValueView renders rows as inline label-value pairs in a vertical
// `space-y-2` list — matching the legacy `<span className="text-chariot-brand-primary
// font-medium">{label}:</span><span className="ml-2">{value}</span>` pattern.
function KeyValueView({ kv }: { kv: ProofKeyValue }) {
  return (
    <div className="space-y-2">
      {(kv.rows ?? []).map((row, i) => (
        <KeyValueRowView key={i} row={row} />
      ))}
    </div>
  );
}

function KeyValueRowView({ row }: { row: ProofKeyValueRow }) {
  const valueClass = cn(
    'text-chariot-text-primary text-sm ml-2',
    row.severity && SEVERITY_TEXT[row.severity]
  );
  return (
    <div className="text-sm">
      <span className="text-chariot-brand-primary font-medium">{row.key}:</span>
      <span className={valueClass}>
        <ValueCell value={row.value} href={row.href} copyable={row.copyable} />
      </span>
    </div>
  );
}

function ValueCell({
  value,
  href,
  copyable,
}: {
  value: string;
  href?: string;
  copyable?: boolean;
}) {
  if (copyable) {
    // Copyable values render as a monospace span with inline copy icon —
    // matches the Detection Rule "Rule ID" treatment in legacy POE.
    return (
      <CopyToClipboard textToCopy={value} alwaysShowCopyIcon>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-chariot-text-link font-mono text-xs break-all underline"
          >
            {value}
          </a>
        ) : (
          <span className="text-chariot-text-secondary font-mono text-xs">
            {value}
          </span>
        )}
      </CopyToClipboard>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-chariot-text-link break-all underline"
      >
        {value}
      </a>
    );
  }
  return <span className="break-all">{value}</span>;
}

function TableView({ table }: { table: ProofTable }) {
  const columns = table.columns ?? [];
  const rows = table.rows ?? [];
  return (
    <div className="border-chariot-stroke overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-chariot-background-secondary">
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                className="text-chariot-text-secondary px-3 py-2 text-left text-xs font-medium tracking-wider uppercase"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className="border-chariot-stroke border-t last:border-b-0"
            >
              {columns.map(col => (
                <td
                  key={col.key}
                  className="text-chariot-text-primary px-3 py-2 align-top"
                >
                  {row[col.key] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ListView matches the legacy "discovered secrets" pattern: bullet-dot +
// rounded background-secondary span, with optional copy + sublabel rules
// alongside.
function ListView({ list }: { list: ProofList }) {
  const items = list.items ?? [];
  if (list.style === 'numbered') {
    return (
      <ol className="text-chariot-text-primary list-decimal space-y-1 pl-5 text-sm">
        {items.map((item, i) => (
          <li key={i}>
            <ListItemText item={item} />
          </li>
        ))}
      </ol>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-3">
          {list.style === 'plain' ? null : (
            <span className="bg-chariot-stroke mt-1.5 flex size-2 shrink-0 items-center justify-center rounded-full" />
          )}
          <ListItemText item={item} />
        </div>
      ))}
    </div>
  );
}

function ListItemText({ item }: { item: ProofList['items'][number] }) {
  const labelNode = item.label ? (
    item.href ? (
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer"
        className="text-chariot-text-link text-sm underline"
      >
        {item.label}
      </a>
    ) : (
      <span className="text-chariot-text-primary text-sm">{item.label}</span>
    )
  ) : null;

  const valueNode =
    item.value !== undefined ? (
      item.copyable ? (
        <CopyToClipboard textToCopy={item.value} alwaysShowCopyIcon>
          <span className="bg-chariot-background-secondary text-chariot-text-primary rounded-md px-2 py-1 font-mono text-xs">
            {item.value}
          </span>
        </CopyToClipboard>
      ) : (
        <span className="bg-chariot-background-secondary text-chariot-text-primary rounded-md px-2 py-1 font-mono text-xs">
          {item.value}
        </span>
      )
    ) : null;

  return (
    <span className="flex flex-wrap items-baseline gap-2">
      {labelNode}
      {valueNode}
      {item.sublabel ? (
        <span className="text-chariot-text-secondary text-xs">
          {item.sublabel}
        </span>
      ) : null}
    </span>
  );
}

const JSON_INDENT = '  ';

// Agent tool-call payloads arrive as a raw JSON string, so a `<pre>` shows the
// escaped wire form (`<`, `\"`, `\r\n`). We re-parse to decode those
// escapes and render with `formatJsonValue`, which (unlike JSON.stringify)
// expands multi-line string values — curl stdout, embedded HTML — into real
// line breaks instead of inline `\r\n`. Non-JSON content is left as-is.
// Recursion is bounded by JSON nesting depth; agent tool-call payloads are
// shallow, so no explicit depth guard is needed.
function prettyPrintIfJson(content: string): string {
  const trimmed = content.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return content;
  try {
    return formatJsonValue(JSON.parse(trimmed), 0);
  } catch {
    return content;
  }
}

function formatJsonValue(value: unknown, depth: number): string {
  if (typeof value === 'string') return formatJsonString(value, depth);
  if (value === null || typeof value !== 'object') return JSON.stringify(value);

  const pad = JSON_INDENT.repeat(depth);
  const padInner = JSON_INDENT.repeat(depth + 1);

  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const items = value.map(v => padInner + formatJsonValue(v, depth + 1));
    return `[\n${items.join(',\n')}\n${pad}]`;
  }

  const entries = Object.entries(value);
  if (entries.length === 0) return '{}';
  const rows = entries.map(
    ([k, v]) =>
      `${padInner}${JSON.stringify(k)}: ${formatJsonValue(v, depth + 1)}`
  );
  return `{\n${rows.join(',\n')}\n${pad}}`;
}

// String values get three treatments, in order:
//   1. JSON-in-a-string (curl returns a JSON body as stdout) → recurse so it
//      renders as nested structure instead of one escaped line.
//   2. Multi-line text → quoted block, each line indented, so headers/HTML
//      read naturally instead of inline `\r\n`.
//   3. Plain single-line → JSON-quoted as-is.
function formatJsonString(value: string, depth: number): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const nested = JSON.parse(trimmed);
      if (nested && typeof nested === 'object') {
        return formatJsonValue(nested, depth);
      }
    } catch {
      // not JSON — fall through to text handling
    }
  }

  // Treat bare CR as a line break too, so CR-only payloads still expand.
  const normalized = value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!normalized.includes('\n')) return JSON.stringify(value);

  const lines = normalized.split('\n');
  // A trailing newline (the norm for curl/HTTP stdout) leaves a final "" that
  // would render as a dangling blank line — drop it.
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();

  const padInner = JSON_INDENT.repeat(depth + 1);
  const body = lines.map(line => padInner + line).join('\n');
  return `"\n${body}\n${JSON_INDENT.repeat(depth)}"`;
}

function CodeBlockView({ code }: { code: ProofCodeBlock }) {
  return (
    <div className="border-chariot-stroke bg-chariot-background-secondary overflow-hidden rounded-md border">
      {code.language ? (
        <div className="text-chariot-text-secondary border-chariot-stroke border-b px-3 py-1 text-xs font-medium tracking-wider uppercase">
          {code.language}
        </div>
      ) : null}
      <pre className="text-chariot-text-primary max-h-96 overflow-auto px-3 py-2 font-mono text-xs whitespace-pre-wrap">
        {prettyPrintIfJson(code.content)}
      </pre>
    </div>
  );
}

function CodeSnippetView({ snippet }: { snippet: ProofCodeSnippet }) {
  const lines = snippet.content.split('\n');
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  const startLine = snippet.start_line ?? 1;

  const highlightAt: Record<number, 'match' | 'context'> = {};
  for (const h of snippet.highlights ?? []) {
    const from = Math.max(1, h.start_line);
    const to = Math.max(from, h.end_line ?? h.start_line);
    for (let l = from; l <= to; l++) {
      highlightAt[l] = h.kind ?? 'match';
    }
  }

  return (
    <div className="border-chariot-stroke bg-chariot-background-secondary overflow-hidden rounded-md border">
      {snippet.file_path ? (
        <div className="text-chariot-text-secondary border-chariot-stroke flex items-center justify-between border-b px-3 py-1 text-xs font-medium">
          <span className="truncate">{snippet.file_path}</span>
          {snippet.language ? (
            <span className="ml-2 tracking-wider uppercase opacity-70">
              {snippet.language}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="max-h-96 overflow-auto">
        <table className="w-full font-mono text-xs">
          <tbody>
            {lines.map((line, i) => {
              const lineNo = i + 1;
              const kind = highlightAt[lineNo];
              const isMatch = kind === 'match';
              return (
                <tr
                  key={i}
                  className={cn(isMatch && 'bg-chariot-status-critical/10')}
                >
                  <td className="text-chariot-text-secondary border-chariot-stroke w-12 border-r px-2 py-0.5 text-right select-none">
                    {startLine + i}
                  </td>
                  <td
                    className={cn(
                      'px-3 py-0.5 whitespace-pre',
                      isMatch
                        ? 'text-chariot-text-red'
                        : 'text-chariot-text-primary'
                    )}
                  >
                    {line || ' '}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HttpExchangeView({ exchange }: { exchange: ProofHttpExchange }) {
  return (
    <div className="space-y-3">
      {exchange.request ? (
        <HttpMessageView title="Request" message={exchange.request} />
      ) : null}
      {exchange.response ? (
        <HttpMessageView title="Response" message={exchange.response} />
      ) : null}
    </div>
  );
}

function HttpMessageView({
  title,
  message,
}: {
  title: string;
  message: NonNullable<ProofHttpExchange['request']>;
}) {
  const startLine =
    title === 'Request'
      ? `${message.method ?? ''} ${message.url ?? ''}`.trim()
      : `HTTP ${message.status_code ?? ''}`;
  const headerLines = (message.headers ?? []).map(h => `${h.name}: ${h.value}`);
  const body = message.body ?? '';
  const wireText = [startLine, ...headerLines, '', body]
    .filter((s, i) => i < 2 || s !== undefined)
    .join('\n');

  return (
    <div className="border-chariot-stroke bg-chariot-background-secondary overflow-hidden rounded-md border">
      <div className="text-chariot-text-secondary border-chariot-stroke border-b px-3 py-1 text-xs font-medium tracking-wider uppercase">
        {title}
        {message.truncated ? (
          <span className="text-chariot-text-yellow ml-2 normal-case">
            (truncated)
          </span>
        ) : null}
      </div>
      <pre className="text-chariot-text-primary max-h-96 overflow-auto px-3 py-2 font-mono text-xs whitespace-pre-wrap">
        {wireText}
      </pre>
    </div>
  );
}

function BannerView({ banner }: { banner: ProofBanner }) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border-l-4 p-3',
        SEVERITY_BORDER[banner.severity],
        SEVERITY_BG[banner.severity]
      )}
    >
      <div className="flex-1">
        <div className="text-chariot-text-primary text-sm font-semibold">
          {banner.title}
        </div>
        {banner.body ? (
          <div className="text-chariot-text-secondary mt-1 text-xs whitespace-pre-wrap">
            {banner.body}
          </div>
        ) : null}
      </div>
      <span
        className={cn(
          'rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase',
          SEVERITY_TEXT[banner.severity]
        )}
      >
        {banner.severity}
      </span>
    </div>
  );
}

function MetricGrid({ metrics }: { metrics: ProofMetric[] }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {metrics.map((m, i) => (
        <div
          key={i}
          className="border-chariot-stroke flex flex-col gap-1 rounded-md border p-3"
        >
          <div className="text-chariot-text-secondary text-xs font-medium tracking-wider uppercase">
            {m.label}
          </div>
          <div
            className={cn(
              'text-chariot-text-primary text-2xl font-semibold',
              m.severity && SEVERITY_TEXT[m.severity]
            )}
          >
            {m.value}
            {m.unit ? (
              <span className="text-chariot-text-secondary ml-1 text-sm font-normal">
                {m.unit}
              </span>
            ) : null}
          </div>
          {m.subtext ? (
            <div className="text-chariot-text-tertiary text-xs">
              {m.subtext}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ReferenceView({ reference }: { reference: ProofReference }) {
  const url = reference.url || derivedReferenceURL(reference);
  const label = reference.label || reference.id || reference.kind;
  const tag = reference.kind.toUpperCase();

  return (
    <div className="text-sm">
      <span className="text-chariot-brand-primary font-medium">{tag}:</span>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-chariot-text-link ml-2 underline"
        >
          {label}
        </a>
      ) : (
        <span className="text-chariot-text-primary ml-2">{label}</span>
      )}
    </div>
  );
}

function derivedReferenceURL(ref: ProofReference): string | undefined {
  if (!ref.id) return undefined;
  switch (ref.kind) {
    case 'cve':
      return `https://nvd.nist.gov/vuln/detail/${ref.id}`;
    case 'cwe': {
      const num = ref.id.replace(/^CWE-/i, '');
      return `https://cwe.mitre.org/data/definitions/${num}.html`;
    }
    case 'mitre':
      return `https://attack.mitre.org/techniques/${ref.id}`;
    default:
      return undefined;
  }
}

function ImageView({ image }: { image: ProofImage }) {
  const src = image.url || image.data_uri;
  if (!src) return null;
  return (
    <figure className="border-chariot-stroke overflow-hidden rounded-md border">
      <img
        src={src}
        alt={image.alt_text ?? ''}
        className="block h-auto w-full"
      />
      {image.caption ? (
        <figcaption className="text-chariot-text-secondary border-chariot-stroke border-t px-3 py-2 text-xs">
          {image.caption}
        </figcaption>
      ) : null}
    </figure>
  );
}

// TimelineView mirrors the legacy "Recent Activity" visual: a vertical
// guide line running through circular nodes, each node holding a title
// (truncated at 80 chars with a tooltip showing the full text), an
// author/timestamp subtitle, and a copyable short-reference badge.
const MAX_TITLE = 80;

function TimelineView({ timeline }: { timeline: ProofTimeline }) {
  const entries = timeline.entries ?? [];
  if (entries.length === 0) return null;
  return (
    <div className="relative">
      <div className="bg-chariot-stroke-secondary absolute top-6 bottom-0 left-3 w-px" />
      <div className="space-y-4">
        {entries.map((entry, idx) => {
          const isLast = idx === entries.length - 1;
          const truncated =
            entry.title.length > MAX_TITLE
              ? `${entry.title.substring(0, MAX_TITLE)}…`
              : entry.title;
          return (
            <div key={idx} className="relative flex items-start space-x-4">
              <div className="relative z-10 flex size-6 items-center justify-center">
                <div className="border-chariot-background-primary bg-chariot-stroke size-3 rounded-full border-2" />
              </div>
              <div className="min-w-0 flex-1 pb-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    {entry.title.length > MAX_TITLE ? (
                      <InfoTooltip
                        title={
                          <div className="max-w-md whitespace-pre-wrap">
                            {entry.title}
                          </div>
                        }
                      >
                        <div className="text-chariot-text-primary cursor-help text-sm leading-5 font-medium">
                          {truncated}
                        </div>
                      </InfoTooltip>
                    ) : entry.href ? (
                      <a
                        href={entry.href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-chariot-text-link text-sm leading-5 font-medium underline"
                      >
                        {truncated}
                      </a>
                    ) : (
                      <div className="text-chariot-text-primary text-sm leading-5 font-medium">
                        {truncated}
                      </div>
                    )}
                    {entry.subtitle ? (
                      <div className="text-chariot-text-tertiary mt-1 text-xs">
                        {entry.subtitle}
                      </div>
                    ) : null}
                  </div>
                  {entry.reference ? (
                    <div className="ml-3 shrink-0">
                      <CopyToClipboard
                        iconClassName="size-3 text-chariot-text-secondary"
                        alwaysShowCopyIcon
                        textToCopy={entry.reference}
                      >
                        <code className="border-chariot-stroke text-chariot-text-secondary rounded border px-2 py-1 font-mono text-xs whitespace-nowrap">
                          {entry.reference}
                        </code>
                      </CopyToClipboard>
                    </div>
                  ) : null}
                </div>
              </div>
              {isLast ? (
                <div className="bg-chariot-background-primary absolute top-6 bottom-0 left-3 w-px" />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
